import { ReactComponent as FathomLogo } from '@/assets/fathom.svg'
import { GoogleAuthButton } from '@/web-app/views/users/components/google-auth-button'
import { MicrosoftAuthButton } from '@/web-app/views/users/components/microsoft-auth-button'
import { SocialProofFooter } from '@/web-app/views/users/components/social-proof-footer'
import { Testimonial } from '@/web-app/views/users/components/testimonial'
import { usePageProps } from '@/shared/hooks/use-page-props'
import { useQueryUrl } from '@/shared/hooks/use-query-url'
import { useAllowlistSearchParams } from '@/web-app/hooks/use-allowlist-search-params'
import { buildUrlWithParams } from '@/web-app/utils/build-url-with-params'
import { addAnalyticsToUrl } from '@/web-app/utils/analytics'

interface ISignUpData {
  googleUrl?: string
  microsoftUrl?: string
  fromGoogle?: boolean
  fromMicrosoft?: boolean
  showInviteBanner?: boolean
  showWorkEmailPrompt?: boolean
  showMissingScopesError?: boolean
  showOopsError?: boolean
  showUnavailableCalendarError?: boolean
  showCalendarNotFoundError?: boolean
  showWorkEmailRequiredError?: boolean
}

export default function SignUp() {
  const { pageProps } = usePageProps<{ signUpDataUrl: string; isIos: boolean }>()

  const { allowedParams } = useAllowlistSearchParams({
    allowedKeys: ['invite', 'invite_source', 'ps_partner_key', 'questionnaire'],
  })

  const signUpDataUrlWithParams =
    pageProps?.signUpDataUrl && Object.keys(allowedParams).length > 0
      ? buildUrlWithParams(pageProps.signUpDataUrl, allowedParams as Record<string, string>)
      : pageProps?.signUpDataUrl

  const { data: signUpData, isLoading: isLoadingSignUpData } = useQueryUrl<ISignUpData>(signUpDataUrlWithParams)
  const {
    googleUrl,
    microsoftUrl,
    fromGoogle,
    fromMicrosoft,
    showInviteBanner,
    showWorkEmailPrompt,
    showMissingScopesError,
    showOopsError,
    showUnavailableCalendarError,
    showCalendarNotFoundError,
    showWorkEmailRequiredError,
  } = signUpData ?? {}

  if (isLoadingSignUpData) {
    return <div className='fv:flex fv:flex-col fv:min-h-screen fv:bg-black fv:overflow-auto' />
  }

  return (
    <div className='fv:flex fv:flex-col fv:min-h-screen fv:bg-black fv:overflow-auto'>
      {/* Invite Banner */}
      {showInviteBanner && (
        <div className='fv:p-3 fv:w-full fv:text-lg fv:font-medium fv:text-center fv:text-black fv:bg-amber-300'>
          🎁 <span className='fv:font-bold'>Referral Bonus:</span> Sign up now to unlock two months of our most advanced
          AI meeting summaries for free.
        </div>
      )}

      <div className='fv:flex fv:justify-center fv:pt-11 fv:pb-22'>
        <a href={addAnalyticsToUrl('https://fathom.ai')}>
          <FathomLogo className='fv:w-44 fv:text-off-white' />
        </a>
      </div>

      {/* Main content area */}
      <div className='fv:flex fv:flex-col fv:lg:flex-row fv:justify-center fv:lg:justify-between fv:items-center fv:flex-1 fv:w-full fv:max-w-screen-xl fv:mx-auto fv:px-4 fv:lg:px-25'>
        {/* Card */}
        <div className='fv:relative fv:flex fv:flex-col fv:items-center fv:w-full fv:lg:w-auto fv:max-w-lg fv:h-fit fv:rounded-3xl fv:border fv:border-off-white/25 fv:bg-neutral-900/80 fv:backdrop-blur-sm fv:px-5 fv:pt-14 fv:pb-8'>
          {/* Emoji + Title */}
          <span className='fv:text-3xl'>🚀</span>
          {showWorkEmailPrompt ? (
            <h1 className='fv:mt-2 fv:text-3xl fv:font-semibold fv:text-amber-400 fv:text-center'>
              Please sign in with your <span className='fv:font-extrabold'>work</span> account
            </h1>
          ) : (
            <h1 className='fv:mt-2 fv:text-3xl fv:font-bold fv:text-off-white fv:text-center'>Sign up for Fathom</h1>
          )}

          {/* Subtitle */}
          {!showWorkEmailPrompt && (
            <p className='fv:mt-8 fv:text-sm fv:text-off-white fv:text-center'>
              Connect your work email to get started in minutes
            </p>
          )}

          {/* Auth buttons */}
          <div className='fv:mt-12 fv:flex fv:flex-col fv:gap-4'>
            <GoogleAuthButton
              authUrl={googleUrl}
              label='Continue with Google'
              fromGoogle={fromGoogle}
              showCalendarNotFoundError={showCalendarNotFoundError}
              showMissingScopesError={showMissingScopesError}
              showOopsError={showOopsError}
              showUnavailableCalendarError={showUnavailableCalendarError}
              showWorkEmailRequiredError={showWorkEmailRequiredError}
            />
            <MicrosoftAuthButton
              authUrl={microsoftUrl}
              label='Continue with Microsoft'
              fromMicrosoft={fromMicrosoft}
              showCalendarNotFoundError={showCalendarNotFoundError}
              showMissingScopesError={showMissingScopesError}
              showOopsError={showOopsError}
              showUnavailableCalendarError={showUnavailableCalendarError}
              showWorkEmailRequiredError={showWorkEmailRequiredError}
            />
          </div>

          {/* Sign in link, only if not coming from iOS */}
          {!pageProps?.isIos && (
            <div className='fv:mt-10 fv:text-sm fv:text-off-white fv:text-center'>
              <p>
                Already have a Fathom account?{' '}
                <a href='/users/sign_in' className='fv:text-accent-fathom fv:underline'>
                  Sign in
                </a>
              </p>
            </div>
          )}

          {/* Terms */}
          <p className='fv:mt-auto fv:pt-10 fv:text-xs fv:text-white/40 fv:text-center'>
            By using Fathom, you agree to the{' '}
            <a href='/terms' className='fv:underline'>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href='/privacy' className='fv:underline'>
              Privacy Policy
            </a>
            .
          </p>
        </div>

        {/* Testimonial */}
        <div className='fv:flex fv:flex-1 fv:items-center fv:justify-center fv:pt-32 fv:pb-14 fv:lg:py-0 fv:px-14'>
          <Testimonial />
        </div>
      </div>

      {/* Bottom social proof bar */}
      <div className='fv:py-11'>{!pageProps?.isIos && <SocialProofFooter />}</div>
    </div>
  )
}
